import { useState } from 'react';
import { useFile, useSelection } from '../../context/AppContext.jsx';
import { useMassEdit } from '../../hooks/useMassEdit.js';

export default function MassEditSection() {
  const { nodes } = useFile();
  const { multiSelectedIndices } = useSelection();
  const { applyMassEdit } = useMassEdit();
  const [scope, setScope] = useState('all');
  const [selectedNode, setSelectedNode] = useState('');
  const [offsets, setOffsets] = useState({
    x: 0, y: 0, z: 0,
    rx: 0, ry: 0, rz: 0,
  });
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="mb-6">
      <h2
        className="text-lg font-semibold mb-4 pb-2 border-b-2 border-border cursor-pointer flex items-center justify-between hover:text-primary transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 17a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2zM14 17a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z" />
          </svg>
          <span>Mass Edit</span>
        </span>
        <span className={`text-base text-text-secondary transition-transform ${isExpanded ? '' : '-rotate-90'}`}>▼</span>
      </h2>
      
      {isExpanded && (
        <>
          <p className="text-text-secondary text-sm mb-4">Apply offset to waypoints by node or selection</p>
          
          <div className="space-y-4">
        <div>
          <label className="text-sm font-semibold text-text-secondary mb-1 block">Apply to:</label>
          <select
            value={scope}
            onChange={(e) => {
              setScope(e.target.value);
              setSelectedNode('');
            }}
            className="w-full bg-bg-tertiary border border-border rounded px-2 py-1.5 text-text-primary text-sm"
          >
            <option value="all">All Waypoints</option>
            <option value="selected">Selected Waypoints</option>
            <option value="node">Node</option>
          </select>
        </div>

        {scope === 'node' && (
          <div>
            <label className="text-sm font-semibold text-text-secondary mb-1 block">Node:</label>
            <select
              value={selectedNode}
              onChange={(e) => setSelectedNode(e.target.value)}
              className="w-full bg-bg-tertiary border border-border rounded px-2 py-1.5 text-text-primary text-sm"
            >
              <option value="">Select a node...</option>
              {nodes.map((node, index) => (
                <option key={index} value={index}>
                  {node.type} - {node.feature} ({node.indices.length} waypoints)
                </option>
              ))}
            </select>
          </div>
        )}

        {scope === 'selected' && (
          <div className="text-sm text-text-secondary">
            {multiSelectedIndices.size > 0 ? (
              <span>{multiSelectedIndices.size} waypoint(s) selected</span>
            ) : (
              <span className="text-error">No waypoints selected. Use Ctrl+Click to select multiple.</span>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-medium">Position Offset (mm)</h4>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">ΔX:</label>
            <input
              type="number"
              step="0.1"
              value={offsets.x}
              onChange={(e) => setOffsets({ ...offsets, x: parseFloat(e.target.value) || 0 })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">mm</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">ΔY:</label>
            <input
              type="number"
              step="0.1"
              value={offsets.y}
              onChange={(e) => setOffsets({ ...offsets, y: parseFloat(e.target.value) || 0 })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">mm</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">ΔZ:</label>
            <input
              type="number"
              step="0.1"
              value={offsets.z}
              onChange={(e) => setOffsets({ ...offsets, z: parseFloat(e.target.value) || 0 })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">mm</span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-medium">Rotation Offset (degrees)</h4>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">ΔRX:</label>
            <input
              type="number"
              step="0.1"
              value={offsets.rx}
              onChange={(e) => setOffsets({ ...offsets, rx: parseFloat(e.target.value) || 0 })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">°</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">ΔRY:</label>
            <input
              type="number"
              step="0.1"
              value={offsets.ry}
              onChange={(e) => setOffsets({ ...offsets, ry: parseFloat(e.target.value) || 0 })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">°</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">ΔRZ:</label>
            <input
              type="number"
              step="0.1"
              value={offsets.rz}
              onChange={(e) => setOffsets({ ...offsets, rz: parseFloat(e.target.value) || 0 })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">°</span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => {
              const nodeIndex = scope === 'node' ? parseInt(selectedNode) : null;
              if (scope === 'node' && (isNaN(nodeIndex) || nodeIndex < 0)) {
                return;
              }
              applyMassEdit(offsets, scope, nodeIndex);
            }}
            className="flex-1 bg-primary text-white px-3 py-1.5 text-sm rounded hover:bg-blue-600 transition-colors"
          >
            Apply Offset
          </button>
          <button
            onClick={() => setOffsets({ x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0 })}
            className="flex-1 bg-bg-tertiary text-text-primary px-3 py-1.5 text-sm rounded hover:bg-bg-hover transition-colors"
          >
            Reset Values
          </button>
        </div>
          </div>
        </>
      )}
    </section>
  );
}

